<!--
  ObjectsPanel.vue — the "Objects" tracker panel (matches go.lacak.io /#/online/trackers).
  Header (collapse + "Objects" + settings gear) → the gear reveals a sort/display toolbar
  (list group-toggle · sort_by_alpha=by-name · dot=by-status · track_changes=by-distance ·
  gps_fixed=set-reference-point · more_vert=display-settings with 7 toggles). Then a
  select-all + Quick search row, a collapsible "Main group (N)", and the 42px tiles: each a
  checkbox + label + INLINE SPEED (moving units) + a status glyph (orange/green/red dot or a
  clock for idle). Hover a tile → an info activator + a ⋮ menu whose six actions are taken
  VERBATIM from the current live go.lacak.io deployment: Share location, Change icon, Chat
  with the objects, Open alert rules, Open device settings, Open reports. (NB: the older
  RECOVERY Pass-7 bundle analysis lists a different set — change-icon/chat/device-settings/
  events/reports/delete; the deployed app has since changed, and the live app is authoritative
  here.) Clicking a tile focuses/follows it; the info activator opens the widgets panel.
  Reconstructed from the live panel + RECOVERY Pass-7 (analysis/online-trackers/*).
-->
<template>
  <div class="objects-panel" :class="{ resizing }">
    <div class="op-header">
      <button class="op-icon-btn" v-tip="'Collapse'" @click="$emit('collapse')"><i class="material-icons">menu_open</i></button>
      <h3 class="op-title">Objects</h3>
      <button class="op-icon-btn" :class="{ on: showToolbar }" v-tip="'Settings'" @click="showToolbar = !showToolbar"><i class="material-icons">settings</i></button>
    </div>

    <!-- sort / display toolbar (revealed by the gear) — icons + order match go.lacak.io -->
    <div v-if="showToolbar" class="op-toolbar">
      <button class="op-tb" :class="{ on: ungrouped }" v-tip="ungrouped ? 'Show groups' : 'Ungroup (flat list)'" @click="toggleGrouping"><i class="material-icons">list</i></button>
      <button class="op-tb" :class="{ on: sort==='name' }" v-tip="'Sort by name'" @click="setSort(sort==='name' ? 'none' : 'name')"><i class="material-icons">sort_by_alpha</i></button>
      <button class="op-tb op-tb-dot" :class="{ on: sort==='status' }" v-tip="'Sort by status'" @click="setSort(sort==='status' ? 'none' : 'status')"><i class="material-icons">lens</i></button>
      <button class="op-tb" :class="{ on: sort==='distance' }" v-tip="'Sort by distance to the reference point'" @click="setSort(sort==='distance' ? 'none' : 'distance')"><i class="material-icons">track_changes</i></button>
      <button class="op-tb" :class="{ on: hasRef }" v-tip="'Set the reference point'" @click="$emit('pick-reference')"><i class="material-icons">gps_fixed</i></button>
      <div class="op-tb-more">
        <button class="op-tb" :class="{ on: showDisplay }" v-tip="'Settings'" @click="showDisplay = !showDisplay"><i class="material-icons">more_vert</i></button>
        <div v-if="showDisplay" class="op-menu op-display-menu" @click.stop>
          <label class="op-menu-check"><input type="checkbox" :checked="display.cluster" @change="toggleDisplay('cluster')" /> Clusterize object markers</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.labels" @change="toggleDisplay('labels')" /> Objects labels</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.tails" @change="toggleDisplay('tails')" /> Trace</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.animation" @change="toggleDisplay('animation')" /> Animation</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.showOnlySelected" @change="toggleDisplay('showOnlySelected')" /> Show only selected objects</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.hideOffline" @change="toggleDisplay('hideOffline')" /> Hide offline objects</label>
          <label class="op-menu-check"><input type="checkbox" :checked="display.showInfoByClick" @change="toggleDisplay('showInfoByClick')" /> Show info by click</label>
        </div>
      </div>
    </div>

    <div class="op-searchrow">
      <label class="op-checkbox">
        <input type="checkbox" :checked="allSelected" @change="toggleAll" />
        <span class="material-icons" :class="{ ind: masterCheckIcon === 'indeterminate_check_box' }">{{ masterCheckIcon }}</span>
      </label>
      <!-- tag-chip combobox: pick tags (coloured chips) or type free text (dark chips); terms
           AND-match id / label / tag names. Max 10 terms. (real search-field v-combobox). -->
      <div class="op-search" :class="{ focused: searchFocused }" @click="focusSearchInput">
        <i class="material-icons op-search-ico">search</i>
        <span v-for="(term, i) in searchTerms" :key="i" class="op-chip" :style="{ background: term.color, color: chipText(term.color) }">
          {{ term.name }}<i class="material-icons" @click.stop="removeTerm(i)">close</i>
        </span>
        <input
          ref="searchInput"
          v-model="searchInput"
          type="text"
          maxlength="50"
          :placeholder="searchTerms.length ? '' : 'Quick search'"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @keydown.enter.prevent="commitInput"
          @keydown.delete="onSearchBackspace"
        />
        <i v-if="searchTerms.length || searchInput" class="material-icons op-search-clear" v-tip="'Clear'" @click.stop="clearSearch">close</i>
      </div>
      <!-- suggestions: matching tags (already-selected hidden) + a free-text "add" tile -->
      <div v-if="searchFocused && (searchSuggestions.length || showNoDataTile)" class="op-search-suggest" @mousedown.prevent>
        <div v-for="tag in searchSuggestions" :key="tag" class="op-suggest-item" @click="pickTag(tag)">
          <span class="op-suggest-dot" :style="{ background: tagColorMap[tag] }"></span>{{ tag }}
        </div>
        <div v-if="showNoDataTile" class="op-suggest-item op-suggest-new" @click="commitInput">
          <i class="material-icons">add</i>Search “{{ searchInput.trim() }}”
        </div>
      </div>
    </div>

    <div class="op-scroll">
      <div v-for="grp in groupedList" :key="grp.id" class="op-groupwrap">
        <div class="op-group" @click="toggleGroup(grp.id)">
          <span class="material-icons op-check-icon" @click.stop="toggleGroupAll(grp)">{{ groupChecked(grp) }}</span>
          <span class="op-group-label">{{ grp.title }} ({{ groupCount(grp) }})</span>
          <span class="material-icons">{{ collapsedGroups[grp.id] ? 'expand_more' : 'expand_less' }}</span>
        </div>

        <template v-if="!collapsedGroups[grp.id]">
          <div
            v-for="t in grp.trackers"
            :key="t.id"
            class="op-tile"
            :class="{ selected: !!selected[t.id], active: activeId === t.id }"
            :style="{ borderLeftColor: groupColorById[t.group_id] || 'transparent' }"
            @click="focusRow(t)"
          >
            <span class="material-icons op-check-icon" :class="{ disabled: t.source && t.source.blocked }" @click.stop="toggle(t)">{{ selected[t.id] ? 'check_box' : 'check_box_outline_blank' }}</span>
            <span class="op-tile-label" :title="t.label">{{ t.label }}</span>
            <!-- trailing cluster — at rest: [speed] [chat?] [status]; on hover the speed + status
                 SWAP for [info_outline] [chat?] [more_vert] in the same slots (real tracker-tile). -->
            <button class="op-row-info" v-tip="'Details'" @click.stop="$emit('show-widgets', t)"><i class="material-icons">info_outline</i></button>
            <span v-if="speedOf(t)" class="op-speed">{{ speedOf(t) }}</span>
            <button v-if="chatUnread[t.id]" class="op-chat-badge" v-tip="'Unread messages'" @click.stop="$emit('chat', t)">
              <i class="material-icons">chat_bubble</i><span class="op-badge">{{ chatUnread[t.id] > 99 ? '99+' : chatUnread[t.id] }}</span>
            </button>
            <span class="op-status">
              <i v-if="connStatus(t) === 'just_registered'" class="material-icons op-jr">access_time</i>
              <i v-else-if="connStatus(t) === 'blocked'" class="material-icons op-balance" v-tip="'Balance low'">monetization_on</i>
              <i v-else class="op-dot" :class="'conn-' + connStatus(t)"></i>
            </span>
            <button class="op-row-menu" v-tip="'Actions'" @click.stop="openMenu(t, $event)"><i class="material-icons">more_vert</i></button>
          </div>
        </template>
      </div>
      <div v-if="!filtered.length" class="op-empty">No objects match your search.</div>
    </div>

    <!-- row context menu — actions/icons/labels VERBATIM from go.lacak.io -->
    <div v-if="menuFor" class="op-rowmenu" :style="menuStyle" @click.stop>
      <div class="rm-item" @click="rowAction('share')"><i class="material-icons">share</i> Share location</div>
      <div class="rm-item" @click="rowAction('change-icon')"><i class="material-icons">wallpaper</i> Change icon</div>
      <div class="rm-item" @click="rowAction('chat')"><i class="material-icons">chat_bubble_outline</i> Chat with the objects</div>
      <div class="rm-item" @click="rowAction('alert-rules')"><i class="material-icons">notifications</i> Open alert rules</div>
      <div class="rm-item" @click="rowAction('device-settings')"><i class="material-icons">build</i> Open device settings</div>
      <div class="rm-item" @click="rowAction('reports')"><i class="material-icons">insert_chart_outlined</i> Open reports</div>
    </div>

    <!-- track-history square FAB (bottom-right of the panel; appears when an object is
         selected). Sits inside the panel so it follows the resizable panel height. -->
    <button v-if="activeId != null" class="op-history-fab" :class="{ on: historyActive }" v-tip="'Track history'" @click.stop="$emit('history')">
      <i class="material-icons">history</i>
    </button>

    <!-- drag the right edge to resize the panel (updates the shared --panel-w) -->
    <div class="op-resize" @mousedown.prevent="startResize"></div>
  </div>
</template>

<script>
export default {
  name: 'ObjectsPanel',
  props: {
    trackers: { type: Array, default: () => [] },
    states: { type: Object, default: () => ({}) },
    activeId: { type: [Number, String], default: null },
    groups: { type: Array, default: () => [] },
    chatUnread: { type: Object, default: () => ({}) },
    // Reference point {lat,lng} for "sort by distance" (set via the gps_fixed tool → map click)
    refPoint: { type: Object, default: null },
    // whether track-history is currently active (drives the history FAB's active state)
    historyActive: { type: Boolean, default: false }
  },
  data() {
    return {
      // quick-search combobox: committed terms ({name, color}) + the live typed text.
      searchTerms: [], searchInput: '', searchFocused: false,
      resizing: false,
      collapsedGroups: {}, selected: {},
      showToolbar: false, showDisplay: false,
      sort: 'none', ungrouped: false,
      // display toggles mirror the real "more" menu; defaults match go.lacak.io (labels/
      // trace/clustering/animation ON, the rest OFF).
      display: { labels: true, tails: true, cluster: true, animation: true, follow: true, showOnlySelected: false, hideOffline: false, showInfoByClick: false },
      menuFor: null, menuStyle: {}
    }
  },
  computed: {
    // Every committed term + the live text, AND-matched against id / label / tag names (prefix/
    // substring, case-insensitive) — the real Lunr {bool:'AND', expand:true} over id+label+tags.
    filtered() {
      const terms = this.searchTerms.map((t) => t.name.toLowerCase())
      const live = this.searchInput.trim().toLowerCase()
      const all = live ? terms.concat([live]) : terms
      if (!all.length) return this.trackers
      return this.trackers.filter((t) => {
        const id = String(t.id).toLowerCase()
        const label = (t.label || '').toLowerCase()
        const tags = (t.tags || []).map((x) => String(x).toLowerCase())
        return all.every((term) => id.indexOf(term) !== -1 || label.indexOf(term) !== -1 || tags.some((tag) => tag.indexOf(term) !== -1))
      })
    },
    // The account's tags (dropdown source) — unique across the roster, sorted.
    allTags() {
      const set = {}
      this.trackers.forEach((t) => (t.tags || []).forEach((tag) => { if (tag) set[tag] = true }))
      return Object.keys(set).sort()
    },
    // Stable colour per tag (real chips are tag-coloured; the demo roster has no tag colours).
    tagColorMap() {
      const PAL = ['#1e88c7', '#8e44ad', '#16a085', '#e67e22', '#c0392b', '#2e7d32', '#00838f', '#5e35b1', '#d81b60']
      const m = {}
      this.allTags.forEach((tag, i) => { m[tag] = PAL[i % PAL.length] })
      return m
    },
    // Tag suggestions matching the typed text, with already-picked tags hidden (hide-selected).
    searchSuggestions() {
      const q = this.searchInput.trim().toLowerCase()
      const picked = {}
      this.searchTerms.forEach((t) => { picked[t.name.toLowerCase()] = true })
      return this.allTags.filter((tag) => !picked[tag.toLowerCase()] && (!q || tag.toLowerCase().indexOf(q) !== -1)).slice(0, 8)
    },
    // Offer a free-text "add" tile when the typed text isn't an exact existing tag/term (hide-no-data:!input).
    showNoDataTile() {
      const q = this.searchInput.trim()
      if (!q || this.searchTerms.length >= 10) return false
      const low = q.toLowerCase()
      const isTag = this.allTags.some((tag) => tag.toLowerCase() === low)
      const already = this.searchTerms.some((t) => t.name.toLowerCase() === low)
      return !isTag && !already
    },
    hasRef() { return !!(this.refPoint && typeof this.refPoint.lat === 'number') },
    // group_id → '#rrggbb' (groups carry a hex colour without the '#'); drives the per-row
    // left stripe, matching the real tile's group-colour edge.
    groupColorById() {
      const m = {}
      this.groups.forEach((g) => { if (g && g.color != null) m[g.id] = g.color[0] === '#' ? g.color : '#' + g.color })
      return m
    },
    sorted() {
      const arr = this.filtered.slice()
      if (this.sort === 'name') arr.sort((a, b) => (a.label || '').localeCompare(b.label || ''))
      else if (this.sort === 'status') {
        // Real sort order (269b.js:966): active, idle, signal_lost, offline, just_registered.
        const rank = { active: 0, idle: 1, signal_lost: 2, offline: 3, just_registered: 4, blocked: 5 }
        arr.sort((a, b) => (rank[this.connStatus(a)] ?? 9) - (rank[this.connStatus(b)] ?? 9) || (a.label || '').localeCompare(b.label || ''))
      } else if (this.sort === 'distance' && this.hasRef) {
        // nearest-first from the reference point; objects without a fix sink to the bottom
        arr.sort((a, b) => {
          const da = this.distanceFromRef(a); const db = this.distanceFromRef(b)
          if (da == null && db == null) return (a.label || '').localeCompare(b.label || '')
          if (da == null) return 1
          if (db == null) return -1
          return da - db
        })
      }
      return arr
    },
    // Group the (filtered+sorted) trackers by group_id, in the tracker/group/list order.
    // When "ungrouped" (list toggle), show a single flat pseudo-group.
    groupedList() {
      if (this.ungrouped) {
        return this.sorted.length ? [{ id: '__all', title: 'All objects', trackers: this.sorted }] : []
      }
      const defs = this.groups.length ? this.groups : [{ id: 0, title: 'Main group' }]
      const byId = {}
      defs.forEach((g) => { byId[g.id] = { id: g.id, title: g.title, trackers: [] } })
      if (!byId[0]) byId[0] = { id: 0, title: 'Main group', trackers: [] }
      this.sorted.forEach((t) => {
        const gid = (t.group_id != null && byId[t.group_id]) ? t.group_id : 0
        byId[gid].trackers.push(t)
      })
      const order = defs.map((g) => g.id)
      if (order.indexOf(0) === -1) order.unshift(0)
      return order.map((id) => byId[id]).filter((g) => g && g.trackers.length)
    },
    // Select-all operates on non-blocked ("active") trackers only (source: activeTrackers).
    selectable() { return this.filtered.filter((t) => !(t.source && t.source.blocked)) },
    allSelected() { return this.selectable.length > 0 && this.selectable.every((t) => this.selected[t.id]) },
    masterCheckIcon() {
      if (!this.selectable.length) return 'check_box_outline_blank'
      if (this.allSelected) return 'check_box'
      return this.selectable.some((t) => this.selected[t.id]) ? 'indeterminate_check_box' : 'check_box_outline_blank'
    }
  },
  methods: {
    // The row status dot reflects CONNECTION health (connection_status), not movement.
    // Real registerIcon (269b.js:3636): just_registered → clock; blocked → monetization_on;
    // everything else → a `lens` dot coloured by connection_status
    // (active #69cf01 / idle #41b81a / signal_lost·offline #fe6700). Movement is shown only
    // as the inline speed and on the map marker shape — never as the row dot.
    connStatus(t) {
      if (t.source && t.source.blocked) return 'blocked'
      const s = this.states[t.id]
      const cs = (s && s.connection_status) || t.connection_status
      return cs || 'offline'
    },
    speedOf(t) {
      const s = this.states[t.id]
      const spd = s && s.gps && s.gps.speed   // speed is a gps-level field (real get_states)
      return spd ? spd + ' km/h' : ''
    },
    // Location of a tracker from its live state (for distance sort).
    latlngOf(t) {
      const s = this.states[t.id]
      const loc = s && s.gps && s.gps.location
      return loc && typeof loc.lat === 'number' ? loc : null
    },
    // Great-circle distance (m) from the reference point; null when no fix / no ref.
    distanceFromRef(t) {
      if (!this.hasRef) return null
      const loc = this.latlngOf(t); if (!loc) return null
      const R = 6371000; const toRad = (d) => d * Math.PI / 180
      const dLat = toRad(loc.lat - this.refPoint.lat); const dLng = toRad(loc.lng - this.refPoint.lng)
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(this.refPoint.lat)) * Math.cos(toRad(loc.lat)) * Math.sin(dLng / 2) ** 2
      return 2 * R * Math.asin(Math.sqrt(a))
    },
    // ---- panel width drag-resize (updates the shared --panel-w; persisted) ----
    startResize(e) {
      const screen = this.$el && this.$el.closest('.online-screen')
      if (!screen) return
      // Tear down any prior session first — a drag whose mouseup was missed (released off-window)
      // leaves listeners attached; without this, re-dragging would orphan the old mousemove and
      // resize forever.
      if (this._onResize) this.stopResize()
      this.resizing = true
      this._resizeScreen = screen
      this._resizeStartX = e.clientX
      this._resizeStartW = this.$el.getBoundingClientRect().width
      this._onResize = (ev) => this.onResize(ev)
      this._stopResize = () => this.stopResize()
      document.addEventListener('mousemove', this._onResize)
      document.addEventListener('mouseup', this._stopResize)
    },
    onResize(e) {
      const w = Math.max(280, Math.min(560, this._resizeStartW + (e.clientX - this._resizeStartX)))
      this._resizeScreen.style.setProperty('--panel-w', w + 'px')
    },
    stopResize() {
      this.resizing = false
      document.removeEventListener('mousemove', this._onResize)
      document.removeEventListener('mouseup', this._stopResize)
      this._onResize = null; this._stopResize = null
      try { const w = this._resizeScreen.style.getPropertyValue('--panel-w'); if (w) localStorage.setItem('tracko-panel-w', w) } catch (e) { /* */ }
    },
    // ---- quick-search combobox ----
    focusSearchInput() { if (this.$refs.searchInput) this.$refs.searchInput.focus() },
    // Text colour that reads on a chip's background (light bg → dark text, dark bg → white).
    chipText(bg) {
      const h = String(bg || '').replace('#', '')
      if (h.length < 6) return '#fff'
      const r = parseInt(h.slice(0, 2), 16); const g = parseInt(h.slice(2, 4), 16); const b = parseInt(h.slice(4, 6), 16)
      return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? '#263238' : '#fff'
    },
    // Commit the typed text as a term (Enter / free-text tile). A term matching an existing tag
    // takes that tag's colour; a novel term is a dark ("free text") chip. Enforces the 10-term cap.
    commitInput() {
      const name = this.searchInput.trim()
      if (!name) return
      if (this.searchTerms.length >= 10) { this.searchInput = ''; return }
      const low = name.toLowerCase()
      if (this.searchTerms.some((t) => t.name.toLowerCase() === low)) { this.searchInput = ''; return }
      this.searchTerms.push({ name, color: this.tagColorMap[name] || (this.allTags.some((tag) => tag.toLowerCase() === low) ? '#607d8b' : '#455a64') })
      this.searchInput = ''
    },
    pickTag(tag) {
      if (this.searchTerms.length < 10 && !this.searchTerms.some((t) => t.name.toLowerCase() === tag.toLowerCase())) {
        this.searchTerms.push({ name: tag, color: this.tagColorMap[tag] || '#607d8b' })
      }
      this.searchInput = ''
      this.$nextTick(() => this.focusSearchInput())
    },
    removeTerm(i) { this.searchTerms.splice(i, 1) },
    clearSearch() { this.searchTerms = []; this.searchInput = ''; this.focusSearchInput() },
    // Backspace on an empty input removes the last chip.
    onSearchBackspace() { if (!this.searchInput && this.searchTerms.length) this.searchTerms.pop() },
    // Delay hiding the dropdown so a suggestion click registers first; a (re)focus cancels the
    // pending hide (else a mousedown-blur → synchronous refocus, e.g. clicking the clear ×, would
    // still fire the stale timer ~150ms later and close the dropdown while the input has focus).
    onSearchFocus() { clearTimeout(this._blurTimer); this.searchFocused = true },
    onSearchBlur() { this._blurTimer = setTimeout(() => { this.searchFocused = false }, 150) },
    setSort(s) {
      // "Sort by distance" needs a reference point first (matches the real toast + flow).
      if (s === 'distance' && !this.hasRef) {
        this._pendingDistance = true
        this.$emit('need-reference')
        return
      }
      // Choosing any other sort abandons a pending distance request, so a later reference
      // point can't silently hijack the sort back to 'distance'.
      this._pendingDistance = false
      this.sort = s
      try { localStorage.setItem('tracker-list-sort', s) } catch (e) { /* */ }
    },
    toggleGrouping() {
      this.ungrouped = !this.ungrouped
      try { localStorage.setItem('tracker-list-ungrouped', this.ungrouped ? '1' : '0') } catch (e) { /* */ }
    },
    toggleDisplay(key) {
      this.display = Object.assign({}, this.display, { [key]: !this.display[key] })
      this.$emit('display-change', Object.assign({}, this.display))
    },
    toggle(t) {
      // Blocked units aren't selectable (select-all/master all exclude them); ignore so the
      // master checkbox and the emitted selection can never desync.
      if (t.source && t.source.blocked) return
      this.$set(this.selected, t.id, !this.selected[t.id]); this.emitSelection()
    },
    // Row-body click = the real focusTracker: EXCLUSIVE select — clear the whole checkbox set,
    // then tick only this row — then let the parent fit the map to it (Path A: no follow, no
    // widgets). The checkbox tick + "Show only selected" both read this same set.
    focusRow(t) {
      // Blocked units are non-interactive in the real app (pointer-events:none). Clicking their
      // row does nothing — focusing one would also pan the map to a marker the "Show only
      // selected" filter keeps hidden (blocked can never enter the selection set).
      if (t.source && t.source.blocked) return
      this.selected = { [t.id]: true }; this.emitSelection()
      this.$emit('focus', t)
    },
    toggleAll() {
      const target = !this.allSelected
      this.selectable.forEach((t) => this.$set(this.selected, t.id, target))
      this.emitSelection()
    },
    // Publish the set of checked trackers so the map can honour "Show only selected".
    emitSelection() {
      const ids = {}
      Object.keys(this.selected).forEach((id) => { if (this.selected[id]) ids[id] = true })
      this.$emit('selection-change', ids)
    },
    // "(N)" when none selected, "(sel/N)" when some selected (matches source).
    groupCount(grp) {
      const sel = grp.trackers.filter((t) => this.selected[t.id]).length
      return sel > 0 ? sel + '/' + grp.trackers.length : String(grp.trackers.length)
    },
    toggleGroup(id) {
      this.$set(this.collapsedGroups, id, !this.collapsedGroups[id])
      try { localStorage.setItem('tracker-list-collapsed', JSON.stringify(this.collapsedGroups)) } catch (e) { /* */ }
    },
    groupSelectable(grp) { return grp.trackers.filter((t) => !(t.source && t.source.blocked)) },
    groupChecked(grp) {
      const some = this.groupSelectable(grp).some((t) => this.selected[t.id])
      // "Full" requires EVERY tile in the group selected — so a group holding an
      // (unselectable) blocked unit stays indeterminate, matching its "(sel/total)" count
      // instead of showing a full box next to a fractional count.
      const all = grp.trackers.length && grp.trackers.every((t) => this.selected[t.id])
      return all ? 'check_box' : some ? 'indeterminate_check_box' : 'check_box_outline_blank'
    },
    toggleGroupAll(grp) {
      const sel = this.groupSelectable(grp)
      const target = !sel.every((t) => this.selected[t.id])
      sel.forEach((t) => this.$set(this.selected, t.id, target))
      this.emitSelection()
    },
    openMenu(t, ev) {
      if (this.menuFor && this.menuFor.id === t.id) { this.menuFor = null; return }
      const r = ev.currentTarget.getBoundingClientRect()
      this.menuFor = t
      this.menuStyle = { top: r.bottom + 'px', left: Math.max(8, r.right - 180) + 'px' }
    },
    rowAction(action) {
      const t = this.menuFor
      this.menuFor = null
      this.$emit('row-action', { action, tracker: t })
    },
    closeMenu() { this.menuFor = null }
  },
  watch: {
    // Once a reference point is set after a "sort by distance" attempt, apply the sort.
    // If the reference point is cleared while distance-sorting, fall back to no sort so the
    // toolbar highlight can't claim "sorted by distance" over an unsorted list.
    refPoint() {
      if (this._pendingDistance && this.hasRef) { this.sort = 'distance'; this._pendingDistance = false }
      else if (!this.hasRef && this.sort === 'distance') this.sort = 'none'
    }
  },
  mounted() {
    document.addEventListener('click', this.closeMenu)
    // Restore persisted list prefs (grouping / sort / collapsed groups).
    try {
      this.ungrouped = localStorage.getItem('tracker-list-ungrouped') === '1'
      const s = localStorage.getItem('tracker-list-sort'); if (s) this.sort = s
      const c = localStorage.getItem('tracker-list-collapsed'); if (c) this.collapsedGroups = JSON.parse(c) || {}
      // Restore the persisted panel width (drag-resize) onto the shared --panel-w var — re-clamp
      // to the drag bounds so a stale/garbage stored value can't break the layout.
      const px = parseFloat(localStorage.getItem('tracko-panel-w'))
      if (Number.isFinite(px)) { const scr = this.$el.closest('.online-screen'); if (scr) scr.style.setProperty('--panel-w', Math.max(280, Math.min(560, px)) + 'px') }
    } catch (e) { /* */ }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
    clearTimeout(this._blurTimer)
    if (this._onResize) { document.removeEventListener('mousemove', this._onResize); document.removeEventListener('mouseup', this._stopResize) }
  }
}
</script>

<style scoped>
/* Content-height card that the user can STRETCH vertically (real panel: resize:vertical).
   Height fits the list up to a viewport cap; drag the bottom-right handle to resize. */
/* Full-height dock on the left (real tt-wrapper panel), not a content-height card. A drag
   handle on the right edge resizes the shared --panel-w (LIVE minimized-panel behaviour). */
.objects-panel { position: absolute; top: 12px; bottom: 12px; left: 12px; width: var(--panel-w, 320px); background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.22); display: flex; flex-direction: column; z-index: 600; font-family: Roboto, 'Helvetica Neue', Arial, sans-serif; overflow: hidden; }
.op-resize { position: absolute; top: 0; bottom: 0; right: 0; width: 7px; cursor: ew-resize; z-index: 2; }
.op-resize::after { content: ''; position: absolute; top: 50%; right: 2px; width: 3px; height: 34px; transform: translateY(-50%); border-radius: 2px; background: #cfd8dc; opacity: 0; transition: opacity .15s; }
.op-resize:hover::after, .objects-panel.resizing .op-resize::after { opacity: 1; }
.objects-panel.resizing { user-select: none; }
.op-header { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border-bottom: 1px solid #eceff1; }
.op-title { flex: 1; margin: 0; font-size: 18px; font-weight: 500; color: #263238; }
.op-icon-btn { background: none; border: none; color: #78909c; cursor: pointer; display: flex; padding: 2px; }
.op-icon-btn.on { color: #1e88c7; }
.op-toolbar { display: flex; align-items: center; gap: 2px; padding: 4px 10px; border-bottom: 1px solid #eceff1; }
.op-tb { background: none; border: none; width: 34px; height: 32px; cursor: pointer; color: #78909c; display: flex; align-items: center; justify-content: center; border-radius: 4px; }
.op-tb:hover { background: #f2f4f6; }
.op-tb.on { color: #1e88c7; background: #e8f4fb; }
.op-tb .material-icons { font-size: 19px; }
.op-tb-dot .material-icons { font-size: 13px; color: #9d9d9d; }
.op-tb-dot.on .material-icons { color: #1e88c7; }
.op-tb-more { position: relative; margin-left: auto; }
.op-menu { position: absolute; background: #fff; border-radius: 6px; box-shadow: 0 2px 10px rgba(0,0,0,.22); z-index: 40; padding: 6px 0; }
.op-display-menu { top: 34px; right: 0; width: 232px; }
.op-menu-check { display: flex; align-items: center; gap: 8px; padding: 7px 14px; font-size: 13px; color: #37474f; cursor: pointer; white-space: nowrap; }
.op-searchrow { position: relative; display: flex; align-items: flex-start; gap: 8px; padding: 8px 12px; border-bottom: 1px solid #eceff1; }
.op-searchrow > .op-checkbox { margin-top: 8px; }
.op-checkbox input { display: none; }
.op-checkbox .material-icons, .op-check-icon { color: #90a4ae; font-size: 20px; cursor: pointer; }
.op-check-icon.disabled { color: #cfd8dc; cursor: default; }
.op-checkbox .material-icons.ind { color: #1e88c7; }
.op-search { flex: 1; display: flex; flex-wrap: wrap; align-items: center; gap: 4px; min-height: 36px; background: #f4f6f8; border-radius: 6px; padding: 3px 8px; cursor: text; }
.op-search.focused { background: #eef4f8; box-shadow: inset 0 0 0 1px #b3d4e6; }
.op-search-ico { color: #90a4ae; font-size: 20px; flex: none; }
.op-search input { flex: 1; min-width: 60px; border: none; background: none; outline: none; padding: 4px 2px; font-size: 14px; color: #37474f; }
.op-search-clear { cursor: pointer; color: #90a4ae; font-size: 18px; flex: none; }
.op-search-clear:hover { color: #607d8b; }
.op-chip { display: inline-flex; align-items: center; gap: 3px; height: 22px; padding: 0 4px 0 9px; border-radius: 11px; font-size: 12px; font-weight: 500; white-space: nowrap; }
.op-chip .material-icons { font-size: 15px; cursor: pointer; opacity: .8; }
.op-chip .material-icons:hover { opacity: 1; }
.op-search-suggest { position: absolute; top: calc(100% - 4px); left: 42px; right: 12px; z-index: 30; background: #fff; border-radius: 6px; box-shadow: 0 3px 14px rgba(0,0,0,.22); padding: 4px 0; max-height: 240px; overflow-y: auto; }
.op-suggest-item { display: flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 14px; color: #37474f; cursor: pointer; }
.op-suggest-item:hover { background: #f2f6f9; }
.op-suggest-dot { width: 10px; height: 10px; border-radius: 50%; flex: none; }
.op-suggest-new { color: #1e88c7; }
.op-suggest-new .material-icons { font-size: 18px; }
/* margin-right keeps the list's scrollbar out of the resize strip's 7px band on the right edge,
   so dragging the scrollbar thumb doesn't grab the resize handle instead. */
.op-scroll { flex: 1 1 auto; min-height: 0; margin-right: 8px; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: #cfd8dc transparent; }
.op-scroll::-webkit-scrollbar { width: 8px; }
.op-scroll::-webkit-scrollbar-track { background: transparent; }
.op-scroll::-webkit-scrollbar-thumb { background: #cfd8dc; border-radius: 4px; border: 2px solid #fff; }
.op-scroll::-webkit-scrollbar-thumb:hover { background: #b0bec5; }
/* track-history square FAB (Navixy "square-fab"): rounded square, not a circle */
.op-history-fab { position: absolute; right: 16px; bottom: 20px; width: 56px; height: 56px; border: none; border-radius: 8px; background: #1e96dc; color: #fff; cursor: pointer; box-shadow: 0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12); display: flex; align-items: center; justify-content: center; z-index: 10; }
.op-history-fab.on { background: #0d5a8a; }
.op-history-fab .material-icons { font-size: 24px; }
.op-group { display: flex; align-items: center; gap: 8px; padding: 8px 14px; cursor: pointer; font-weight: 500; color: #37474f; position: sticky; top: 0; z-index: 2; background: #fff; border-bottom: 1px solid #f0f2f4; }
.op-group-label { flex: 1; font-size: 14px; }
/* border-left is the per-row group-colour stripe (set inline); active/selected use background. */
.op-tile { display: flex; align-items: center; gap: 8px; height: 42px; padding: 0 8px 0 12px; cursor: pointer; border-bottom: 1px solid #f4f6f8; border-left: 3px solid transparent; }
.op-tile:hover { background: #f3f6f8; }
.op-tile.selected { background: #eef6fb; }
.op-tile.active { background: #e3f1f8; }
/* Row name + speed on the real go.lacak.io 14px-primary / 12px-secondary body scale
   (tracker-tile__title = 14px), so the list and the widgets panel share one type scale. */
.op-tile-label { flex: 1; font-size: 14px; color: #37474f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.op-speed { font-size: 12px; color: #607d8b; white-space: nowrap; }
.op-chat-badge { position: relative; background: none; border: none; cursor: pointer; padding: 0 2px; display: flex; align-items: center; color: #1e88c7; }
.op-chat-badge .material-icons { font-size: 18px; }
.op-badge { position: absolute; top: -4px; right: -6px; min-width: 15px; height: 15px; padding: 0 3px; border-radius: 8px; background: #e53935; color: #fff; font-size: 9px; font-weight: 600; line-height: 15px; text-align: center; box-sizing: border-box; }
.op-status { width: 16px; display: flex; justify-content: center; }
.op-dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }
/* connection_status palette — VERBATIM from Trackers2.css .tracker-tile__status--* */
.op-dot.conn-active { background: #69cf01; }
.op-dot.conn-idle { background: #41b81a; }
.op-dot.conn-signal_lost, .op-dot.conn-offline { background: #fe6700; }
.op-jr { color: #888; font-size: 18px; }              /* just_registered clock */
.op-balance { color: #fe6700; font-size: 18px; }      /* blocked → balance warning */
.op-row-menu, .op-row-info { background: none; border: none; color: #90a4ae; cursor: pointer; padding: 0; display: none; }
/* the swap: on hover reveal info_outline + more_vert and hide the speed + status they replace */
.op-tile:hover .op-row-menu, .op-tile:hover .op-row-info { display: flex; }
.op-tile:hover .op-speed, .op-tile:hover .op-status { display: none; }
.op-row-menu:hover, .op-row-info:hover { color: #1e88c7; }
.op-row-menu .material-icons, .op-row-info .material-icons { font-size: 18px; }
.op-empty { padding: 16px 14px; color: #90a4ae; font-size: 13px; }
.op-rowmenu { position: fixed; width: 180px; background: #fff; border-radius: 6px; box-shadow: 0 3px 14px rgba(0,0,0,.25); z-index: 800; padding: 6px 0; }
.rm-item { display: flex; align-items: center; gap: 10px; padding: 9px 14px; font-size: 14px; color: #37474f; cursor: pointer; }
.rm-item:hover { background: #f2f6f9; }
.rm-item .material-icons { font-size: 18px; color: #78909c; }
</style>
